/**
 * ERROR BOUNDARY COMPONENT
 * Catches React errors and displays a fallback UI
 */

import { Component, ReactNode } from 'react'
import { GlassCard } from './ui/GlassCard'
import { AlertCircle } from 'lucide-react'
import { Button } from './ui/Button'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: any
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ error, errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary p-8 flex items-center justify-center">
          <GlassCard className="max-w-2xl w-full p-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red/10 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red" />
              </div>
              <h2 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
                Something went wrong
              </h2>
              <p className="text-body text-light-text-secondary dark:text-dark-text-secondary mb-6">
                An error occurred while loading this page. Please try refreshing or contact support if the issue persists.
              </p>

              {this.state.error && (
                <details className="text-left mb-6 p-4 bg-red/5 border border-red/10 rounded-xl">
                  <summary className="text-subheadline font-medium text-red cursor-pointer mb-2">
                    Error Details
                  </summary>
                  <div className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary font-mono">
                    <p className="mb-2 font-bold">{this.state.error.toString()}</p>
                    {this.state.errorInfo && (
                      <pre className="whitespace-pre-wrap overflow-auto max-h-64">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}

              <div className="flex gap-3 justify-center">
                <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
                <Button
                  onClick={() => window.location.href = '/'}
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      )
    }

    return this.props.children
  }
}
